
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import { useActivities, useCreateActivity, useUpdateActivity } from "@/hooks/useActivities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Plus, Clock, User, CheckCircle2, Circle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function ActivitiesPage() {
  const { isAuthenticated } = useAuth();
  const { currentProject } = useProject();
  const navigate = useNavigate();
  
  const [selectedStage, setSelectedStage] = useState<number>(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newActivity, setNewActivity] = useState({
    name: '',
    description: '',
    assignedTo: '',
    dueDate: new Date(),
    stage: 0,
    isRequired: true
  });

  const { data: activities = [], isLoading } = useActivities(currentProject?.id);
  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
    if (!currentProject) {
      navigate("/projects");
    }
  }, [isAuthenticated, currentProject, navigate]);

  const ribaStages = [
    { id: 0, name: 'Strategic Definition' },
    { id: 1, name: 'Preparation and Briefing' },
    { id: 2, name: 'Concept Design' },
    { id: 3, name: 'Spatial Coordination' },
    { id: 4, name: 'Technical Design' },
    { id: 5, name: 'Manufacturing and Construction' },
    { id: 6, name: 'Handover' },
    { id: 7, name: 'Use' }
  ];

  const filteredActivities = activities.filter(activity => activity.stage === selectedStage);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'in-progress':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateActivity = async () => {
    if (!currentProject) return;

    try {
      await createActivity.mutateAsync({
        ...newActivity,
        projectId: currentProject.id
      });
      setIsCreateDialogOpen(false);
      setNewActivity({
        name: '',
        description: '',
        assignedTo: '',
        dueDate: new Date(),
        stage: selectedStage,
        isRequired: true
      });
      toast.success("Activity created successfully");
    } catch (error) {
      toast.error("Failed to create activity");
      console.error('Error creating activity:', error);
    }
  };

  const handleUpdateStatus = async (activityId: string, newStatus: 'not-started' | 'in-progress' | 'completed') => {
    if (!currentProject) return;

    try {
      await updateActivity.mutateAsync({
        id: activityId,
        projectId: currentProject.id,
        status: newStatus,
        dateCompleted: newStatus === 'completed' ? new Date() : undefined
      });
      toast.success("Activity updated successfully");
    } catch (error) {
      toast.error("Failed to update activity");
      console.error('Error updating activity:', error);
    }
  };

  if (!currentProject) return null;

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      
      <main className="flex-1 container py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Activities</h1>
            <p className="text-gray-500">{currentProject.name} - RIBA Stage Management</p>
          </div>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> New Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create New Activity</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Activity Name</Label>
                  <Input
                    id="name"
                    value={newActivity.name}
                    onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                    placeholder="Enter activity name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newActivity.description}
                    onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                    placeholder="Enter activity description"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="stage">RIBA Stage</Label>
                  <Select
                    value={newActivity.stage.toString()}
                    onValueChange={(value) => setNewActivity({...newActivity, stage: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ribaStages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id.toString()}>
                          Stage {stage.id}: {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="assignedTo">Assigned To</Label>
                  <Input
                    id="assignedTo"
                    value={newActivity.assignedTo}
                    onChange={(e) => setNewActivity({...newActivity, assignedTo: e.target.value})}
                    placeholder="Enter assignee name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !newActivity.dueDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newActivity.dueDate ? format(newActivity.dueDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newActivity.dueDate}
                        onSelect={(date) => date && setNewActivity({...newActivity, dueDate: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateActivity} disabled={!newActivity.name}>
                  Create Activity
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* RIBA Stage Selector */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {ribaStages.map((stage) => (
              <Button
                key={stage.id}
                variant={selectedStage === stage.id ? "default" : "outline"}
                onClick={() => setSelectedStage(stage.id)}
                className="flex-shrink-0"
              >
                Stage {stage.id}: {stage.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Activities List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-12">
              <p>Loading activities...</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                <h3 className="text-xl font-medium mb-2">No activities for Stage {selectedStage}</h3>
                <p className="text-gray-500 mb-6">Create activities to track progress in this RIBA stage</p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Create First Activity
                </Button>
              </div>
            </div>
          ) : (
            filteredActivities.map((activity) => (
              <Card key={activity.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(activity.status)}
                      <div>
                        <CardTitle className="text-lg">{activity.name}</CardTitle>
                        {activity.description && (
                          <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={getStatusColor(activity.status)}>
                      {activity.status.replace('-', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    {activity.assignedTo && (
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>{activity.assignedTo}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>Due: {format(activity.dueDate, "MMM dd, yyyy")}</span>
                    </div>
                    {activity.isRequired && (
                      <Badge variant="outline" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                  
                  {activity.status !== 'completed' && (
                    <div className="flex gap-2 mt-4">
                      {activity.status === 'not-started' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(activity.id, 'in-progress')}
                        >
                          Start Activity
                        </Button>
                      )}
                      {activity.status === 'in-progress' && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(activity.id, 'completed')}
                        >
                          Mark Complete
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {activity.dateCompleted && (
                    <p className="text-sm text-green-600 mt-2">
                      Completed on {format(activity.dateCompleted, "MMM dd, yyyy")}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
